import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import { ApiError, isUnauthorized, RequestTimeout } from "./api"

/** 403 `{ error: "not_member" }`: the person is no longer a member of the workspace (earlier settings spec §1.4). */
export const isNotMember = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 403 && error.body?.error === "not_member"

/** 403 `{ error: "forbidden" }` (or any other 403): a role check failed; the role may have changed. */
export const isForbidden = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 403 && error.body?.error !== "not_member"

/** What a 403 `forbidden` reads as, everywhere. */
export const FORBIDDEN_TEXT = "You don't have permission to do that. Your role may have changed."

/**
 * One QueryClient for the app. `onUnauthorized` runs whenever any query or mutation fails with 401, so an expired
 * session is handled in one place instead of every page showing "unauthorized". `onForbidden` runs for every 403
 * (`not_member` or a role failure), so membership and roles are re-read in one place too.
 */
export const createQueryClient = ({
  onUnauthorized,
  onForbidden = () => {},
}: {
  onUnauthorized: () => void
  onForbidden?: (notMember: boolean) => void
}) => {
  const onError = (error: unknown) => {
    if (isUnauthorized(error)) onUnauthorized()
    else if (error instanceof ApiError && error.status === 403) onForbidden(isNotMember(error))
  }
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay,
        // Data a page just read stays fresh for 30 s (no refetch on every mount); the socket pushes live changes.
        staleTime: 30_000,
        // Keep unused data for a day, so going back to a page shows it at once while it refreshes.
        gcTime: 24 * 60 * 60_000,
        refetchOnWindowFocus: false,
      },
    },
  })
}

/** Retries after a failed query: at most two. */
export const MAX_RETRIES = 2
/** The longest wait before a retry. */
export const MAX_RETRY_DELAY_MS = 8_000

/**
 * Retry twice for network or server trouble. Never a 4xx answer (it won't change on retry) and never a timeout (the
 * request already waited its full time; the page shows the error with a way to try again).
 */
export const shouldRetry = (failureCount: number, error: unknown): boolean =>
  failureCount < MAX_RETRIES &&
  !(error instanceof RequestTimeout) &&
  !(error instanceof ApiError && error.status >= 400 && error.status < 500)

/**
 * Exponential backoff with jitter: after failure n the wait is between half and all of 2^n s (at most 8 s), so clients
 * that failed together (a deploy, a dropped link) don't all retry at the same moment.
 */
export const retryDelay = (
  failureCount: number,
  _error?: unknown,
  random: () => number = Math.random,
): number => {
  const ceiling = Math.min(MAX_RETRY_DELAY_MS, 1_000 * 2 ** Math.max(0, failureCount))
  return Math.round(ceiling / 2 + random() * (ceiling / 2))
}
